"use client"

import { useState, useEffect, useRef } from "react"
import axios from "axios"
import Swal from "sweetalert2"

import { format } from "date-fns"
import L from "leaflet"
import { TimePicker } from 'rsuite';
import 'rsuite/TimePicker/styles/index.css';
import "leaflet/dist/leaflet.css"

// Creamos una instancia de axios para las peticiones autenticadas
const authAxios = axios.create({
  baseURL: "https://app.transorientesas.com/api",
  headers: {
    "Content-Type": "application/json",
  },
})

// Interceptor para añadir el token a las peticiones
authAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Estilos para el TimePicker
const timePickerStyles = `
  .react-time-picker {
    width: 100% !important;
  }
  
  .react-time-picker__wrapper {
    border: 1px solid #d1d5db !important;
    border-radius: 0.375rem !important;
    padding: 0.5rem 0.75rem !important;
  }
  
  .react-time-picker__inputGroup {
    min-width: 0 !important;
  }
  
  .react-time-picker__inputGroup__input {
    color: #1f2937 !important;
    font-size: 0.875rem !important;
    padding: 0 !important;
  }
  
  .react-time-picker__button {
    padding: 0 0.5rem !important;
  }
  
  .react-time-picker__button:enabled:hover .react-time-picker__button__icon {
    stroke: #3b82f6 !important;
  }
`;

// Agregar los estilos al documento
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = timePickerStyles;
  document.head.appendChild(styleElement);
}

export default function ServiceForm() {
  const [clients, setClients] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [items, setItems] = useState([])
  const [isMapModalOpen, setIsMapModalOpen] = useState(false)
  const [currentAddressField, setCurrentAddressField] = useState("")
  const [mapPosition, setMapPosition] = useState({ lat: 4.6097, lng: -74.0817 }) // Bogotá por defecto
  const [mapSearchAddress, setMapSearchAddress] = useState("")
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markerRef = useRef(null)
  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false)
  const [routeCoordinates, setRouteCoordinates] = useState([])
  const [routeDistance, setRouteDistance] = useState("")
  const [routeDuration, setRouteDuration] = useState("")
  const routeMapRef = useRef(null)
  const routeMapInstanceRef = useRef(null)
  const [createdServices, setCreatedServices] = useState([]) // Nuevo estado para servicios creados

  // Verificar si el expediente debe autogenerarse
  const isAutogenerado = localStorage.getItem('autogenerado') === '1';

  // Función para formatear la hora a formato 24 horas
  const formatTimeTo24H = (timeString) => {
    if (!timeString) return '';
    
    // Si ya está en formato 24h (HH:MM), devolverlo tal cual
    if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString)) {
      // Asegurar que las horas tengan 2 dígitos
      const [hours, minutes] = timeString.split(':');
      return `${hours.padStart(2, '0')}:${minutes}`;
    }
    
    // Si tiene formato 12h (h:MM AM/PM), convertirlo a 24h
    if (timeString.includes(' ')) {
      const [time, modifier] = timeString.split(' ');
      let [hours, minutes] = time.split(':');
      
      if (hours === '12') {
        hours = modifier === 'AM' ? '00' : '12';
      } else if (modifier === 'PM') {
        hours = (parseInt(hours, 10) + 12).toString();
      }
      
      return `${hours.padStart(2, '0')}:${minutes}`;
    }
    
    return timeString; // Si no coincide con ningún formato conocido, devolverlo tal cual
  };
  
  // Función para manejar cambios en los inputs del formulario
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Función para manejar la selección de un cliente del modal
  const handleClientSelect = async (clientId) => {
    try {
      const ide = localStorage.getItem("idusuario")
    if (typeof ide !== "undefined") {
    } else {
      const idea = sessionStorage.getItem("idusuario")
      const ide = idea
    }
      setIsLoading(true);
      const response = await authAxios.post("master/buscar-cliente", {
        identificacion: clientId,
        ide:ide
      });
      
      const clientData = response.data.data;
      
      setFormValues(prev => ({
        ...prev,
        nombre_clientes: clientId,
        nombreCliente: clientData.nombre || "",
        nombreUsuario: clientData.nombre || "",
        apellidoUsuario: clientData.apellido || "",
        correoUsuario: clientData.correo || "",
        telefonoUsuario: clientData.telefono || "",
        ciudad: clientData.ciudad || "BOGOTA D.C"
      }));
      
      setIsClientDialogOpen(false);
    } catch (error) {
      console.error("Error al cargar los datos del cliente:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar los datos del cliente",
        icon: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Función para manejar cambios en el campo de hora
  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    // Asegurar que el valor esté en formato HH:MM
    let formattedValue = value;
    
    // Si el valor no está vacío, formatearlo a HH:MM
    if (value) {
      // Si el valor tiene el formato correcto (HH:MM), usarlo directamente
      if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value)) {
        const [hours, minutes] = value.split(':');
        formattedValue = `${hours.padStart(2, '0')}:${minutes}`;
      } 
      // Si el valor tiene formato AM/PM, convertirlo a 24 horas
      else if (value.includes(' ')) {
        const [time, modifier] = value.split(' ');
        let [hours, minutes] = time.split(':');
        
        if (modifier === 'PM' && hours !== '12') {
          hours = String(parseInt(hours, 10) + 12);
        } else if (modifier === 'AM' && hours === '12') {
          hours = '00';
        }
        
        formattedValue = `${hours.padStart(2, '0')}:${minutes || '00'}`;
      }
    }
    
    setFormValues(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const [formValues, setFormValues] = useState({
    nombre_clientes: "",
    nombreCliente: "",
    ciudad: "",
    estadoServicio: "",
    tarifa: "",
    tipoRecorrido: "",
    numeroContacto: "",
    numeroExpediente: "",
    fecha: format(new Date(), "yyyy-MM-dd"),
    horaInicial: "",
    direccionInicial: "",
    direccionFinal: "",
    modalidadServicio: "",
    cantidad: "",
    obs: "",
    items: [],
    nombreUsuario: "",
    apellidoUsuario: "",
    correoUsuario: "",
    telefonoUsuario: "",
    metodo_pago: "",
  })

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients()
  }, [])

  // Inicializar el mapa cuando se abre el modal
  useEffect(() => {
    if (isMapModalOpen && typeof window !== "undefined") {
      // Esperar a que el DOM esté listo
      setTimeout(() => {
        initMap()
      }, 100)
    }
  }, [isMapModalOpen])

  // Actualizar el campo de búsqueda del mapa cuando se abre el modal
  useEffect(() => {
    if (isMapModalOpen && currentAddressField) {
      setMapSearchAddress(formValues[currentAddressField] || "")
    }
  }, [isMapModalOpen, currentAddressField, formValues])

  // Inicializar el mapa de ruta cuando se abre el modal
  useEffect(() => {
    if (isRouteModalOpen && typeof window !== "undefined") {
      setTimeout(() => {
        initRouteMap()
      }, 100)
    }
  }, [isRouteModalOpen, routeCoordinates])

  const initMap = () => {
    if (!mapRef.current) return

    // Limpiar el contenedor del mapa si ya existe
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
    }
    mapRef.current.innerHTML = ""

    // Crear el mapa
    const map = L.map(mapRef.current).setView([mapPosition.lat, mapPosition.lng], 13)
    mapInstanceRef.current = map

    // Añadir capa de OpenStreetMap
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map)

    // Añadir marcador
    const marker = L.marker([mapPosition.lat, mapPosition.lng], { draggable: true }).addTo(map)
    markerRef.current = marker

    // Evento al mover el marcador
    marker.on("dragend", async (e) => {
      const position = marker.getLatLng()
      try {
        // Obtener dirección desde las coordenadas (geocodificación inversa)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.lat}&lon=${position.lng}&zoom=18&addressdetails=1`,
          {
            headers: {
              "Accept-Language": "es",
              "User-Agent": "TransOrientesApp/1.0",
            },
          },
        )
        const data = await response.json()

        // Formatear la dirección en estilo colombiano
        const address = formatearDireccionColombia(data)

        // Actualizar el campo de búsqueda del mapa
        setMapSearchAddress(address)

        // Actualizar el campo de dirección correspondiente en el formulario
        setFormValues((prev) => ({
          ...prev,
          [currentAddressField]: address,
        }))
      } catch (error) {
        console.error("Error al obtener la dirección:", error)
      }
    })

    // Evento al hacer clic en el mapa
    map.on("click", async (e) => {
      const { lat, lng } = e.latlng
      marker.setLatLng([lat, lng])

      try {
        // Obtener dirección desde las coordenadas (geocodificación inversa)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
          {
            headers: {
              "Accept-Language": "es",
              "User-Agent": "TransOrientesApp/1.0",
            },
          },
        )
        const data = await response.json()

        // Formatear la dirección en estilo colombiano
        const address = formatearDireccionColombia(data)

        // Actualizar el campo de búsqueda del mapa
        setMapSearchAddress(address)

        // Actualizar el campo de dirección correspondiente en el formulario
        setFormValues((prev) => ({
          ...prev,
          [currentAddressField]: address,
        }))
      } catch (error) {
        console.error("Error al obtener la dirección:", error)
      }
    })

    // Si ya hay una dirección, intentar geocodificarla
    if (formValues[currentAddressField]) {
      searchAddressOnMap(formValues[currentAddressField])
    }
  }

  // Función para formatear direcciones en estilo colombiano
  const formatearDireccionColombia = (nominatimData) => {
    if (!nominatimData || !nominatimData.address) {
      return "Dirección desconocida"
    }

    const address = nominatimData.address

    // Extraer componentes relevantes
    const road = address.road || address.pedestrian || address.street || address.path || ""
    const houseNumber = address.house_number || ""
    const suburb = address.suburb || ""
    const city = address.city || address.town || address.village || address.municipality || ""
    const state = address.state || ""

    // Determinar si es calle o carrera (simplificación)
    let roadType = "Calle"
    if (
      road.toLowerCase().includes("carrera") ||
      road.toLowerCase().includes("cr") ||
      road.toLowerCase().includes("cra")
    ) {
      roadType = "Carrera"
    } else if (road.toLowerCase().includes("avenida") || road.toLowerCase().includes("av")) {
      roadType = "Avenida"
    } else if (road.toLowerCase().includes("diagonal") || road.toLowerCase().includes("diag")) {
      roadType = "Diagonal"
    } else if (road.toLowerCase().includes("transversal") || road.toLowerCase().includes("trans")) {
      roadType = "Transversal"
    }

    // Extraer número de la vía si está incluido en el nombre
    let roadNumber = ""
    const roadParts = road.split(" ")
    for (let i = 0; i < roadParts.length; i++) {
      if (/\d/.test(roadParts[i])) {
        roadNumber = roadParts[i]
        break
      }
    }

    // Construir la dirección en formato colombiano
    let formattedAddress = ""

    if (roadNumber) {
      formattedAddress = `${roadType} ${roadNumber}`
    } else if (road) {
      formattedAddress = `${roadType} ${road}`
    }

    if (houseNumber) {
      formattedAddress += ` # ${houseNumber}`
    }

    // Añadir ciudad y departamento
    const locationParts = []
    if (suburb && !city.includes(suburb)) locationParts.push(suburb)
    if (city) locationParts.push(city)
    if (state) locationParts.push(state)

    if (locationParts.length > 0) {
      formattedAddress += formattedAddress ? `, ${locationParts.join(", ")}` : locationParts.join(", ")
    }

    // Si no se pudo formatear, usar la dirección completa de Nominatim
    if (!formattedAddress.trim()) {
      return nominatimData.display_name
    }

    return formattedAddress
  }

  const fetchClients = async () => {
    setIsLoading(true)
    try {
      let ide = localStorage.getItem("idusuario")
      if (typeof ide !== "undefined") {
      } else {
        const idea = sessionStorage.getItem("idusuario")
        ide = idea
      }
      const response = await authAxios.post("master/buscar-clientes-todos", {
        idusuario: ide, // Aquí pasas la variable en el cuerpo
      })
      // Asegurarse de que clients sea siempre un array
      if (Array.isArray(response.data)) {
        setClients(response.data)
      } else if (response.data && typeof response.data === "object") {
        // Si es un objeto, intentar extraer un array (común en algunas APIs)
        const clientsArray =
          response.data.data || response.data.clients || response.data.results || Object.values(response.data)
        if (Array.isArray(clientsArray)) {
          setClients(clientsArray)
        } else {
          console.error("La respuesta no contiene un array de clientes:", response.data)
          setClients([])
        }
      } else {
        console.error("Formato de respuesta inesperado:", response.data)
        setClients([])
      }
    } catch (error) {
      console.error("Error fetching clients:", error)
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar los clientes",
        icon: "error",
      })
      setClients([])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchClientDetails = async (clientId) => {
    try {
      const response = await authAxios.post("master/buscar-cliente", {
        identificacion: clientId
      })
      const clientData = response.data.data

      setFormValues(prev => ({
        ...prev,
        nombre_clientes: clientId,
        nombreCliente: clientData.nombre,
        nombreUsuario: clientData.nombre || "",
        apellidoUsuario: clientData.apellido || "",
        correoUsuario: clientData.correo || "",
        telefonoUsuario: clientData.telefono || "",
        ciudad: clientData.ciudad || "BOGOTA D.C"
      }))

      setIsClientDialogOpen(false)
    } catch (error) {
      console.error("Error fetching client details:", error)
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar los detalles del cliente",
        icon: "error"
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Función para generar múltiples números de expediente consecutivos
  const generateMultipleExpedientes = async (count) => {
    const expedientes = []
    try {
      for (let i = 0; i < count; i++) {
        const response = await authAxios.post("master/relacion/buscar-expediente")
        const numeroExpediente = response.data.prefijo + response.data.consecutivo
        expedientes.push(numeroExpediente)
      }
      return expedientes
    } catch (error) {
      console.error("Error generating multiple expedition numbers:", error)
      return Array(count).fill("Por generar")
    }
  }

  // Función para regenerar todos los números de expediente de los items
  const regenerateAllExpedientes = async () => {
    if (items.length === 0) return

    try {
      setIsLoading(true)
      Swal.fire({
        title: "Generando expedientes...",
        text: "Por favor espere",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        },
      })

      const nuevosExpedientes = await generateMultipleExpedientes(items.length)

      const updatedItems = items.map((item, index) => ({
        ...item,
        numeroExpediente: nuevosExpedientes[index],
      }))

      setItems(updatedItems)
      setFormValues({
        ...formValues,
        items: updatedItems,
      })

      Swal.close()
      Swal.fire({
        title: "Expedientes Actualizados",
        text: `Se generaron ${nuevosExpedientes.length} números de expediente`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      })
    } catch (error) {
      console.error("Error regenerating expedition numbers:", error)
      Swal.fire({
        title: "Error",
        text: "No se pudieron regenerar los números de expediente",
        icon: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddItem = async () => {
    if (!formValues.fecha || !formValues.horaInicial) {
      Swal.fire({
        title: "Error",
        text: "Por favor seleccione fecha y hora",
        icon: "warning",
      })
      return
    }

    // Si no hay número de expediente, generar uno automáticamente
    let numeroExpedienteParaItem = formValues.numeroExpediente

    if (!numeroExpedienteParaItem) {
      try {
        setIsLoading(true)
        const response = await authAxios.post("master/relacion/buscar-expediente")
        numeroExpedienteParaItem = response.data.prefijo + response.data.consecutivo
      } catch (error) {
        console.error("Error fetching expedition number:", error)
        numeroExpedienteParaItem = "Por generar"
      } finally {
        setIsLoading(false)
      }
    }

    const newItem = {
      date: formValues.fecha,
      time: formValues.horaInicial,
      direccionInicial: formValues.direccionInicial,
      direccionFinal: formValues.direccionFinal,
      numeroExpediente: numeroExpedienteParaItem, // Agregar el número de expediente
    }

    setItems([...items, newItem])
    setFormValues({
      ...formValues,
      items: [...formValues.items, newItem],
      // Limpiar el número de expediente después de agregar un servicio
      numeroExpediente: "",
    })
  }

  const removeItem = (index) => {
    const updatedItems = [...items]
    updatedItems.splice(index, 1)
    setItems(updatedItems)

    const updatedFormItems = [...formValues.items]
    updatedFormItems.splice(index, 1)
    setFormValues({
      ...formValues,
      items: updatedFormItems,
    })
  }

  const saveItem = async () => {
    if (!formValues.nombreUsuario || !formValues.apellidoUsuario || !formValues.telefonoUsuario) {
      Swal.fire({
        title: "Error",
        text: "Por favor complete los datos del usuario",
        icon: "warning",
      })
      return
    }

    if (!formValues.fecha || !formValues.horaInicial || !formValues.direccionInicial || !formValues.direccionFinal) {
      Swal.fire({
        title: "Error",
        text: "Por favor complete los datos del servicio",
        icon: "warning",
      })
      return
    }

    try {
      setIsLoading(true)
      Swal.showLoading()
      let ide = localStorage.getItem("idusuario")
      if (typeof ide === "undefined" || ide === null) {
        const idea = sessionStorage.getItem("idusuario")
        ide = idea
      }
      const username = localStorage.getItem("username")
      const nombre = localStorage.getItem("nombre")
      const admin = localStorage.getItem("admin")
      const idusuario = localStorage.getItem("idusuario")
      const identificacion = localStorage.getItem("identificacion")
      const cliente_id = ide

      const response = await authAxios.post("exterior/guardar-servicios-cliente", {
        ...formValues,
        username,
        nombre,
        admin,
        idusuario,
        identificacion,
        cliente_id,
      })

      // Manejar la respuesta del API para mostrar todos los servicios creados
      const responseData = response.data
      let serviciosCreados = []

      // Verificar diferentes formatos de respuesta
      if (responseData.serviceIds) {
        // Si es un array de IDs
        if (Array.isArray(responseData.serviceIds)) {
          serviciosCreados = responseData.serviceIds.map((id) => ({ expediente: id }))
        } else {
          // Si es un solo ID
          serviciosCreados = [{ expediente: responseData.serviceIds }]
        }
      } else if (responseData.servicios) {
        // Si la respuesta incluye información completa de servicios
        serviciosCreados = responseData.servicios
      } else if (responseData.data) {
        // Si los datos están en una propiedad 'data'
        serviciosCreados = Array.isArray(responseData.data) ? responseData.data : [responseData.data]
      }

      // Actualizar el estado con los servicios creados
      setCreatedServices(serviciosCreados)

      // Crear el HTML para mostrar todos los servicios creados
      const serviciosHTML = serviciosCreados
        .map((servicio, index) => {
          return `
          <div style="padding: 0.75rem; margin: 0.5rem 0; background-color: #f0fff4; border: 1px solid #10b981; border-radius: 0.375rem;">
            <div style="font-weight: 600; color: #065f46;">Servicio ${index + 1}</div>
            <div style="color: #047857;">Expediente: ${servicio.expediente || servicio.numeroExpediente || servicio.id || "N/A"}</div>
            ${servicio.fecha ? `<div style="color: #047857; font-size: 0.875rem;">Fecha: ${servicio.fecha}</div>` : ""}
            ${servicio.hora ? `<div style="color: #047857; font-size: 0.875rem;">Hora: ${servicio.hora}</div>` : ""}
          </div>
        `
        })
        .join("")

      Swal.fire({
        title: "Servicios Creados Exitosamente",
        html: `
          <div style="padding: 1rem; background-color: #f0fff4; border-radius: 0.5rem; text-align: left;">
            <h3 style="font-size: 1.125rem; font-weight: 600; color: #22543d; margin-bottom: 1rem; text-align: center;">
              ${serviciosCreados.length} servicio(s) creado(s) con éxito
            </h3>
            ${serviciosHTML}
          </div>
        `,
        icon: "success",
        confirmButtonText: "Aceptar",
        width: "600px",
      })

      // Reset form
      setFormValues({
        nombre_clientes: "",
        nombreCliente: "",
        ciudad: "",
        estadoServicio: "",
        tarifa: "",
        tipoRecorrido: "",
        numeroContacto: "",
        numeroExpediente: "", // Campo limpio
        fecha: format(new Date(), "yyyy-MM-dd"),
        horaInicial: "",
        direccionInicial: "",
        direccionFinal: "",
        cantidad: "",
        obs: "",
        items: [],
        nombreUsuario: "",
        apellidoUsuario: "",
        correoUsuario: "",
        telefonoUsuario: "",
        metodo_pago: "",
      })
      setItems([])
    } catch (error) {
      console.error("Error saving service:", error)
      Swal.fire({
        title: "Error",
        text: "No se pudo guardar el servicio",
        icon: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const buscarExpediente = async () => {
    try {
      setIsLoading(true)
      Swal.showLoading()

      const response = await authAxios.post("master/relacion/buscar-expediente")

      const numeroExpediente = response.data.prefijo + response.data.consecutivo

      setFormValues({
        ...formValues,
        numeroExpediente,
      })

      Swal.close()
    } catch (error) {
      console.error("Error fetching expedition number:", error)
      Swal.fire({
        title: "Error",
        text: "No se pudo obtener el número de expediente",
        icon: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Función para limpiar el número de expediente
  const clearExpediente = () => {
    setFormValues({
      ...formValues,
      numeroExpediente: "",
    })
  }

  const openMapModal = (fieldName) => {
    setCurrentAddressField(fieldName)
    setIsMapModalOpen(true)
  }

  const closeMapModal = () => {
    setIsMapModalOpen(false)
  }

  const confirmMapLocation = () => {
    // Asegurarse de que la dirección del mapa se guarde en el formulario
    setFormValues((prev) => ({
      ...prev,
      [currentAddressField]: mapSearchAddress,
    }))
    setIsMapModalOpen(false)
  }

  const handleMapSearchInputChange = (e) => {
    setMapSearchAddress(e.target.value)
  }

  // Modificar la función searchAddressOnMap para manejar búsquedas parciales
  const searchAddressOnMap = async (address) => {
    if (!address) return

    try {
      setIsLoading(true)

      // Si la búsqueda es muy corta (menos de 3 caracteres), añadir un contexto geográfico
      let searchQuery = address
      if (address.length < 3) {
        // Añadir "Colombia" como contexto para búsquedas muy cortas
        searchQuery = `${address}, Colombia`
      }

      // Aumentar el límite de resultados para búsquedas parciales
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=10`,
        {
          headers: {
            "Accept-Language": "es",
            "User-Agent": "TransOrientesApp/1.0",
          },
        },
      )
      const data = await response.json()

      if (data && data.length > 0) {
        // Usar el primer resultado
        const { lat, lon } = data[0]

        // Actualizar posición del mapa y marcador
        if (mapInstanceRef.current && markerRef.current) {
          mapInstanceRef.current.setView([lat, lon], 15)
          markerRef.current.setLatLng([lat, lon])
        }

        // Actualizar el estado de mapPosition
        setMapPosition({ lat, lng: lon })

        // Obtener la dirección completa mediante geocodificación inversa
        const reverseResponse = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
          {
            headers: {
              "Accept-Language": "es",
              "User-Agent": "TransOrientesApp/1.0",
            },
          },
        )
        const reverseData = await reverseResponse.json()

        // Formatear la dirección en estilo colombiano
        const formattedAddress = formatearDireccionColombia(reverseData)

        // Actualizar el campo de búsqueda del mapa
        setMapSearchAddress(formattedAddress)
      } else {
        // Intentar con un contexto geográfico más amplio
        const fallbackResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address + ", Colombia")}&limit=1`,
          {
            headers: {
              "Accept-Language": "es",
              "User-Agent": "TransOrientesApp/1.0",
            },
          },
        )
        const fallbackData = await fallbackResponse.json()

        if (fallbackData && fallbackData.length > 0) {
          const { lat, lon } = fallbackData[0]

          // Actualizar posición del mapa y marcador
          if (mapInstanceRef.current && markerRef.current) {
            mapInstanceRef.current.setView([lat, lon], 15)
            markerRef.current.setLatLng([lat, lon])
          }

          // Actualizar el estado de mapPosition
          setMapPosition({ lat, lng: lon })

          // Obtener la dirección completa mediante geocodificación inversa
          const reverseResponse = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
            {
              headers: {
                "Accept-Language": "es",
                "User-Agent": "TransOrientesApp/1.0",
              },
            },
          )
          const reverseData = await reverseResponse.json()

          // Formatear la dirección en estilo colombiano
          const formattedAddress = formatearDireccionColombia(reverseData)

          // Actualizar el campo de búsqueda del mapa
          setMapSearchAddress(formattedAddress)
        } else {
          // Si aún no hay resultados, mostrar un mensaje más amigable
          Swal.fire({
            title: "Sugerencia",
            text: "Intente con una búsqueda más específica o seleccione un punto en el mapa",
            icon: "info",
          })
        }
      }
    } catch (error) {
      console.error("Error al buscar la dirección:", error)
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al buscar la dirección",
        icon: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const searchAddress = () => {
    searchAddressOnMap(mapSearchAddress)
  }

  const filteredClients = Array.isArray(clients)
    ? clients.filter(
        (client) =>
          client.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.identificacion?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : []

  const getCoordinatesFromAddress = async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address + ", Colombia")}&limit=1`,
        {
          headers: {
            "Accept-Language": "es",
            "User-Agent": "TransOrientesApp/1.0",
          },
        },
      )
      const data = await response.json()

      if (data && data.length > 0) {
        return {
          lat: Number.parseFloat(data[0].lat),
          lng: Number.parseFloat(data[0].lon),
        }
      }
      return null
    } catch (error) {
      console.error("Error al obtener coordenadas:", error)
      return null
    }
  }

  const calculateRoute = async () => {
    if (!formValues.direccionInicial || !formValues.direccionFinal) {
      Swal.fire({
        title: "Error",
        text: "Por favor ingrese tanto la dirección inicial como la final",
        icon: "warning",
      })
      return
    }

    // Declare variables in broader scope
    let originCoords = null
    let destCoords = null

    try {
      setIsLoading(true)
      Swal.fire({
        title: "Calculando ruta...",
        text: "Por favor espere mientras calculamos el mejor trayecto",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        },
      })

      // Obtener coordenadas de origen y destino
      originCoords = await getCoordinatesFromAddress(formValues.direccionInicial)
      destCoords = await getCoordinatesFromAddress(formValues.direccionFinal)

      if (!originCoords || !destCoords) {
        Swal.fire({
          title: "Error",
          text: "No se pudieron encontrar las coordenadas de una o ambas direcciones",
          icon: "error",
        })
        return
      }

      // Intentar con diferentes servicios de routing
      let routeCalculated = false

      // Opción 1: Usar OSRM (Open Source Routing Machine) - sin API key requerida
      try {
        const osrmResponse = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${originCoords.lng},${originCoords.lat};${destCoords.lng},${destCoords.lat}?overview=full&geometries=geojson`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          },
        )

        if (osrmResponse.ok) {
          const osrmData = await osrmResponse.json()

          if (osrmData.routes && osrmData.routes.length > 0) {
            const route = osrmData.routes[0]
            const coordinates = route.geometry.coordinates.map((coord) => [coord[1], coord[0]]) // Invertir lng,lat a lat,lng
            const distance = (route.distance / 1000).toFixed(2) // Convertir a km
            const duration = Math.round(route.duration / 60) // Convertir a minutos

            setRouteCoordinates(coordinates)
            setRouteDistance(`${distance} km`)
            setRouteDuration(`${duration} minutos`)
            setIsRouteModalOpen(true)
            Swal.close()
            routeCalculated = true
            return
          }
        }
      } catch (osrmError) {
        console.log("Error con OSRM, intentando con GraphHopper:", osrmError)
      }

      // Opción 2: Usar GraphHopper (servicio gratuito limitado)
      if (!routeCalculated) {
        try {
          const graphHopperResponse = await fetch(
            `https://graphhopper.com/api/1/route?point=${originCoords.lat},${originCoords.lng}&point=${destCoords.lat},${destCoords.lng}&vehicle=car&locale=es&calc_points=true&key=YOUR_GRAPHHOPPER_KEY`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
            },
          )

          if (graphHopperResponse.ok) {
            const graphData = await graphHopperResponse.json()

            if (graphData.paths && graphData.paths.length > 0) {
              const path = graphData.paths[0]
              // Decodificar la geometría de GraphHopper (formato polyline)
              const coordinates = decodePolyline(path.points)
              const distance = (path.distance / 1000).toFixed(2)
              const duration = Math.round(path.time / 60000) // GraphHopper devuelve en milisegundos

              setRouteCoordinates(coordinates)
              setRouteDistance(`${distance} km`)
              setRouteDuration(`${duration} minutos`)
              setIsRouteModalOpen(true)
              Swal.close()
              routeCalculated = true
              return
            }
          }
        } catch (graphError) {
          console.log("Error con GraphHopper, usando ruta directa:", graphError)
        }
      }

      // Fallback: Crear una ruta interpolada más realista
      if (!routeCalculated) {
        const interpolatedRoute = createInterpolatedRoute(originCoords, destCoords)

        // Calcular distancia usando fórmula de Haversine
        const R = 6371 // Radio de la Tierra en km
        const dLat = ((destCoords.lat - originCoords.lat) * Math.PI) / 180
        const dLng = ((destCoords.lng - originCoords.lng) * Math.PI) / 180
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos((originCoords.lat * Math.PI) / 180) *
            Math.cos((destCoords.lat * Math.PI) / 180) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        const distance = R * c

        // Estimar tiempo basado en velocidad promedio urbana (30 km/h)
        const estimatedTime = Math.round((distance / 30) * 60)

        setRouteCoordinates(interpolatedRoute)
        setRouteDistance(`${distance.toFixed(2)} km (ruta estimada)`)
        setRouteDuration(`${estimatedTime} minutos (estimado)`)
        setIsRouteModalOpen(true)
        Swal.close()
      }
    } catch (error) {
      console.error("Error al calcular la ruta:", error)

      // Only show fallback if we have coordinates
      if (originCoords && destCoords) {
        Swal.fire({
          title: "Error",
          text: "No se pudo calcular la ruta. Se mostrará una ruta estimada.",
          icon: "warning",
        }).then(() => {
          // Mostrar ruta directa como último recurso
          const simpleRoute = [
            [originCoords.lat, originCoords.lng],
            [destCoords.lat, destCoords.lng],
          ]

          const R = 6371
          const dLat = ((destCoords.lat - originCoords.lat) * Math.PI) / 180
          const dLng = ((destCoords.lng - originCoords.lng) * Math.PI) / 180
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((originCoords.lat * Math.PI) / 180) *
              Math.cos((destCoords.lat * Math.PI) / 180) *
              Math.sin(dLng / 2) *
              Math.sin(dLng / 2)
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
          const distance = R * c

          setRouteCoordinates(simpleRoute)
          setRouteDistance(`${distance.toFixed(2)} km (línea directa)`)
          setRouteDuration("Tiempo no disponible")
          setIsRouteModalOpen(true)
        })
      } else {
        Swal.fire({
          title: "Error",
          text: "No se pudo calcular la ruta. Verifique las direcciones ingresadas.",
          icon: "error",
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Función auxiliar para crear una ruta interpolada más realista
  const createInterpolatedRoute = (origin, dest) => {
    const points = []
    const steps = 10 // Número de puntos intermedios

    for (let i = 0; i <= steps; i++) {
      const ratio = i / steps
      const lat = origin.lat + (dest.lat - origin.lat) * ratio
      const lng = origin.lng + (dest.lng - origin.lng) * ratio

      // Añadir algo de variación para simular una ruta más realista
      const variation = 0.001 * Math.sin(ratio * Math.PI * 3)
      points.push([lat + variation, lng + variation])
    }

    return points
  }

  // Función para decodificar polyline (formato usado por GraphHopper y Google)
  const decodePolyline = (encoded) => {
    const points = []
    let index = 0
    const len = encoded.length
    let lat = 0
    let lng = 0

    while (index < len) {
      let b,
        shift = 0,
        result = 0
      do {
        b = encoded.charCodeAt(index++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (b >= 0x20)

      const dlat = result & 1 ? ~(result >> 1) : result >> 1
      lat += dlat

      shift = 0
      result = 0
      do {
        b = encoded.charCodeAt(index++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (b >= 0x20)

      const dlng = result & 1 ? ~(result >> 1) : result >> 1
      lng += dlng

      points.push([lat / 1e5, lng / 1e5])
    }

    return points
  }

  const initRouteMap = () => {
    if (!routeMapRef.current || routeCoordinates.length === 0) return

    // Limpiar el contenedor del mapa si ya existe
    if (routeMapInstanceRef.current) {
      routeMapInstanceRef.current.remove()
      routeMapInstanceRef.current = null
    }
    routeMapRef.current.innerHTML = ""

    // Crear el mapa
    const map = L.map(routeMapRef.current)
    routeMapInstanceRef.current = map

    // Añadir capa de OpenStreetMap
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map)

    // Crear la polilínea de la ruta
    const routeLine = L.polyline(routeCoordinates, {
      color: "#3b82f6",
      weight: 4,
      opacity: 0.8,
    }).addTo(map)

    // Agregar marcadores de inicio y fin
    const startIcon = L.divIcon({
      html: '<div style="background-color: #10b981; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px;">A</div>',
      iconSize: [24, 24],
      className: "custom-marker",
    })

    const endIcon = L.divIcon({
      html: '<div style="background-color: #ef4444; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px;">B</div>',
      iconSize: [24, 24],
      className: "custom-marker",
    })

    L.marker(routeCoordinates[0], { icon: startIcon })
      .addTo(map)
      .bindPopup(`<strong>Origen:</strong><br>${formValues.direccionInicial}`)

    L.marker(routeCoordinates[routeCoordinates.length - 1], { icon: endIcon })
      .addTo(map)
      .bindPopup(`<strong>Destino:</strong><br>${formValues.direccionFinal}`)

    // Ajustar la vista del mapa para mostrar toda la ruta
    map.fitBounds(routeLine.getBounds(), { padding: [20, 20] })
  }

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "1rem" }}>
      {/* Card principal */}
      <div
        style={{
          borderRadius: "0.5rem",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(to right, #3b82f6, #4f46e5)",
            padding: "1.5rem",
            color: "white",
            borderTopLeftRadius: "0.5rem",
            borderTopRightRadius: "0.5rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", margin: "0" }}>Creación de Servicio</h2>
              <p style={{ color: "#bfdbfe", marginTop: "0.5rem", marginBottom: "0" }}>
                Complete el formulario para crear un nuevo servicio
              </p>
            </div>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: "0.8" }}
              >
                <rect x="1" y="3" width="15" height="13"></rect>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                <circle cx="18.5" cy="18.5" r="2.5"></circle>
              </svg>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "1.5rem" }}>
          {/* Sección de Datos del Cliente */}
          <div style={{ marginBottom: "2rem" }}>
            <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1.5rem", color: "#3b82f6" }}>
              Datos del Cliente
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "1.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Cliente
                </label>
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setIsClientDialogOpen(true)}
                    style={{
                      width: "100%",
                      padding: "0.5rem 0.75rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      backgroundColor: "white",
                      textAlign: "left",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    disabled={isLoading}
                  >
                    <span>{formValues.nombreCliente || "Seleccionar Cliente"}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ opacity: "0.5" }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Ciudad
                </label>
                <input
                  type="text"
                  id="ciudad"
                  name="ciudad"
                  value={formValues.ciudad}
                  onChange={handleInputChange}
                  readOnly
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "#f9fafb",
                  }}
                />
              </div>
            </div>

            <h4 style={{ fontSize: "1.125rem", fontWeight: "600", marginBottom: "1rem" }}>
              Datos del Usuario a Transportar
            </h4>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "1.5rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Nombre
                </label>
                <input
                  type="text"
                  id="nombreUsuario"
                  name="nombreUsuario"
                  value={formValues.nombreUsuario}
                  onChange={handleInputChange}
                  readOnly
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "#f9fafb",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Apellido
                </label>
                <input
                  type="text"
                  id="apellidoUsuario"
                  name="apellidoUsuario"
                  value={formValues.apellidoUsuario}
                  onChange={handleInputChange}
                  readOnly
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "#f9fafb",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Teléfono
                </label>
                <input
                  type="text"
                  id="telefonoUsuario"
                  name="telefonoUsuario"
                  value={formValues.telefonoUsuario}
                  onChange={handleInputChange}
                  readOnly
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "#f9fafb",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Correo
                </label>
                <input
                  type="text"
                  id="correoUsuario"
                  name="correoUsuario"
                  value={formValues.correoUsuario}
                  onChange={handleInputChange}
                  readOnly
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "#f9fafb",
                  }}
                />
              </div>
            </div>
          </div>

          <hr style={{ margin: "1.5rem 0", border: "none", borderTop: "1px solid #e2e8f0" }} />

          {/* Sección de Datos del Servicio */}
          <div style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1.5rem", color: "#3b82f6" }}>
              Datos del Servicio
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                gap: "1.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Número de Expediente (Opcional)
                </label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    id="numeroExpediente"
                    name="numeroExpediente"
                    value={formValues.numeroExpediente}
                    onChange={handleInputChange}
                    readOnly={isAutogenerado}
                    placeholder={isAutogenerado ? "Auto-generado por el sistema" : "Dejar vacío para auto-generar"}
                    style={{
                      flex: "1",
                      padding: "0.5rem 0.75rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      backgroundColor: isAutogenerado ? "#f9fafb" : "#ffffff",
                      cursor: isAutogenerado ? "not-allowed" : "text"
                    }}
                  />
                
                  <button
                    onClick={clearExpediente}
                    disabled={isLoading}
                    style={{
                      padding: "0.5rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      backgroundColor: "#ef4444",
                      color: "white",
                      cursor: "pointer",
                    }}
                    title="Limpiar número de expediente"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Fecha
                </label>
                <input
                  type="date"
                  id="fecha"
                  name="fecha"
                  value={formValues.fecha}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Hora Inicial
                </label>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  zIndex: 1000
                }}>
                  <style>{
                    `
                    .rs-picker-menu {
                      position: absolute !important;
                      top: 100% !important;
                      left: 0 !important;
                      margin-top: 4px !important;
                      z-index: 1050 !important;
                      width: 100% !important;
                      min-width: 200px !important;
                    }
                    .rs-picker-toolbar {
                      display: flex !important;
                      justify-content: flex-end;
                      padding: 8px !important;
                      border-top: 1px solid #e5e5ea;
                      margin-top: 8px;
                    }
                    .rs-picker-toolbar-ranges {
                      display: flex;
                      gap: 8px;
                    }
                    .rs-picker-toolbar-option-ok,
                    .rs-picker-toolbar-option-cancel {
                      padding: 4px 12px !important;
                      border-radius: 4px !important;
                      font-size: 14px !important;
                    }
                    .rs-picker-toolbar-option-ok {
                      background-color: #3498ff !important;
                      color: white !important;
                    }
                    .rs-picker-toolbar-option-cancel {
                      border: 1px solid #d9d9d9 !important;
                      background-color: #fff !important;
                      color: #333 !important;
                    }
                    .rs-picker-daterange-menu .rs-stack-item {
                      padding: 0 !important;
                    }
                    `
                  }</style>
                  <TimePicker
                    format="HH:mm"
                    value={formValues.horaInicial ? new Date(`1970-01-01T${formValues.horaInicial}`) : null}
                    onChange={(time) => {
                      if (time) {
                        // Redondear a los 15 minutos más cercanos
                        const minutes = time.getMinutes();
                        const roundedMinutes = Math.round(minutes / 15) * 15;
                        time.setMinutes(roundedMinutes);
                        
                        // Formatear la hora
                        const hours = String(time.getHours()).padStart(2, '0');
                        const mins = String(roundedMinutes).padStart(2, '0');
                        const timeString = `${hours}:${mins}`;
                        
                        setFormValues(prev => ({
                          ...prev,
                          horaInicial: timeString
                        }));
                      } else {
                        setFormValues(prev => ({
                          ...prev,
                          horaInicial: ''
                        }));
                      }
                    }}
                    ranges={[]}
                    hideMeridian
                    block
                    style={{
                      width: '100%'
                    }}
                    cleanable={false}
                    step={15}
                    menuStyle={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1050,
                      width: '100%',
                      marginTop: '4px'
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "1.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Dirección Inicial
                </label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    id="direccionInicial"
                    name="direccionInicial"
                    value={formValues.direccionInicial}
                    onChange={handleInputChange}
                    placeholder="Ingrese la dirección de origen"
                    style={{
                      flex: "1",
                      padding: "0.5rem 0.75rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                    }}
                  />
                  <button
                    onClick={() => openMapModal("direccionInicial")}
                    style={{
                      padding: "0.5rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      backgroundColor: "white",
                      cursor: "pointer",
                      color: "#3b82f6",
                    }}
                    title="Seleccionar en el mapa"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Dirección Final
                </label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    id="direccionFinal"
                    name="direccionFinal"
                    value={formValues.direccionFinal}
                    onChange={handleInputChange}
                    placeholder="Ingrese la dirección de destino"
                    style={{
                      flex: "1",
                      padding: "0.5rem 0.75rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                    }}
                  />
                  <button
                    onClick={() => openMapModal("direccionFinal")}
                    style={{
                      padding: "0.5rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      backgroundColor: "white",
                      cursor: "pointer",
                      color: "#3b82f6",
                    }}
                    title="Seleccionar en el mapa"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Botón Visualizar Trayecto */}
            <div style={{ marginBottom: "1.5rem" }}>
              <button
                onClick={calculateRoute}
                disabled={!formValues.direccionInicial || !formValues.direccionFinal || isLoading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0.75rem 1rem",
                  border: "none",
                  borderRadius: "0.375rem",
                  background:
                    formValues.direccionInicial && formValues.direccionFinal
                      ? "linear-gradient(to right, #10b981, #059669)"
                      : "#e5e7eb",
                  color: formValues.direccionInicial && formValues.direccionFinal ? "white" : "#9ca3af",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  cursor: formValues.direccionInicial && formValues.direccionFinal ? "pointer" : "not-allowed",
                  transition: "all 0.2s ease",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: "0.5rem" }}
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                {isLoading ? "Calculando ruta..." : "Visualizar Trayecto"}
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "1.5rem",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Modalidad del servicio
                </label>
                <select
                  id="modalidadServicio"
                  name="modalidadServicio"
                  value={formValues.modalidadServicio}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "white",
                    marginBottom: "1.5rem"
                  }}
                >
                  <option value="">Seleccione una opción</option>
                  <option value="4">Mensual</option>
                  <option value="5">Por Hora</option>
                  <option value="6">Por Recorrido</option>
                  <option value="7">Por Día</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Modalidad de Pago
                </label>
                <select
                  id="metodo_pago"
                  name="metodo_pago"
                  value={formValues.metodo_pago}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "white",
                  }}
                >
                  <option value="">Seleccione una opción</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="Cargar a la cuenta">Cargar a la cuenta</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Cantidad
                </label>
                <input
                  type="number"
                  id="cantidad"
                  name="cantidad"
                  value={formValues.cantidad}
                  onChange={handleInputChange}
                  disabled={formValues.modalidadServicio !== "5"}
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: formValues.modalidadServicio === "5" ? "1px solid #d1d5db" : "1px solid #e5e7eb",
                    borderRadius: "0.375rem",
                    backgroundColor: formValues.modalidadServicio === "5" ? "white" : "#f9fafb",
                    color: formValues.modalidadServicio === "5" ? "#1f2937" : "#9ca3af",
                    cursor: formValues.modalidadServicio === "5" ? "text" : "not-allowed"
                  }}
                  placeholder={formValues.modalidadServicio === "5" ? "Ingrese la cantidad" : "Seleccione 'Por Hora' para habilitar"}
                />
              </div>
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                Observaciones
              </label>
              <textarea
                id="obs"
                name="obs"
                value={formValues.obs}
                onChange={handleInputChange}
                placeholder="Ingrese observaciones adicionales"
                rows={3}
                style={{
                  width: "100%",
                  padding: "0.5rem 0.75rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                }}
              ></textarea>
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <h4 style={{ fontSize: "1.125rem", fontWeight: "600", margin: "0" }}>Programación de Servicios</h4>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {/* {items.length > 0 && (
                    <button
                      onClick={regenerateAllExpedientes}
                      disabled={isLoading}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.375rem 0.75rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        backgroundColor: "#f59e0b",
                        color: "white",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                      title="Regenerar todos los números de expediente"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "0.5rem" }}
                      >
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <polyline points="1 20 1 14 7 14"></polyline>
                        <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                      </svg>
                      Regenerar Expedientes
                    </button>
                  )} */}
                  <button
                    onClick={handleAddItem}
                    disabled={!formValues.fecha || !formValues.horaInicial || isLoading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.375rem 0.75rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      backgroundColor: "white",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginRight: "0.5rem" }}
                    >
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Agregar Servicio
                  </button>
                </div>
              </div>

              {items.length > 0 ? (
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "0.375rem",
                    overflow: "hidden",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ backgroundColor: "#f9fafb" }}>
                      <tr>
                        <th
                          style={{
                            padding: "0.75rem 1rem",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          Expediente
                        </th>
                        <th
                          style={{
                            padding: "0.75rem 1rem",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          Fecha
                        </th>
                        <th
                          style={{
                            padding: "0.75rem 1rem",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          Hora
                        </th>
                        <th
                          style={{
                            padding: "0.75rem 1rem",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          Origen
                        </th>
                        <th
                          style={{
                            padding: "0.75rem 1rem",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          Destino
                        </th>
                        <th
                          style={{
                            padding: "0.75rem 1rem",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          Acción
                        </th>
                      </tr>
                    </thead>
                    <tbody style={{ borderTop: "1px solid #e2e8f0" }}>
                      {items.map((item, index) => (
                        <tr key={index} style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem" }}>
                            <span
                              style={{
                                padding: "0.25rem 0.5rem",
                                backgroundColor: item.numeroExpediente === "Por generar" ? "#fef3c7" : "#dbeafe",
                                color: item.numeroExpediente === "Por generar" ? "#92400e" : "#1e40af",
                                borderRadius: "0.375rem",
                                fontSize: "0.75rem",
                                fontWeight: "500",
                              }}
                            >
                              {item.numeroExpediente || "Sin asignar"}
                            </span>
                          </td>
                          <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem" }}>{item.date}</td>
                          <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem" }}>{item.time}</td>
                          <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem" }}>{item.direccionInicial}</td>
                          <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem" }}>{item.direccionFinal}</td>
                          <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem" }}>
                            <button
                              onClick={() => removeItem(index)}
                              style={{
                                padding: "0.25rem",
                                border: "none",
                                background: "none",
                                color: "#ef4444",
                                cursor: "pointer",
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "1.5rem",
                    border: "1px dashed #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ margin: "0 auto 0.5rem", color: "#9ca3af" }}
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <p style={{ color: "#6b7280", margin: "0 0 0.25rem" }}>No hay servicios programados</p>
                  <p style={{ fontSize: "0.875rem", color: "#9ca3af", margin: "0" }}>
                    Complete los datos del servicio y haga clic en "Agregar Servicio"
                  </p>
                </div>
              )}
            </div>

            {/* Mostrar servicios creados si existen */}
            {createdServices.length > 0 && (
              <div style={{ marginBottom: "1.5rem" }}>
                <h4 style={{ fontSize: "1.125rem", fontWeight: "600", marginBottom: "1rem", color: "#10b981" }}>
                  Servicios Creados Recientemente
                </h4>
                <div
                  style={{
                    border: "1px solid #10b981",
                    borderRadius: "0.375rem",
                    backgroundColor: "#f0fff4",
                    padding: "1rem",
                  }}
                >
                  {createdServices.map((servicio, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "0.75rem",
                        margin: "0.5rem 0",
                        backgroundColor: "white",
                        border: "1px solid #10b981",
                        borderRadius: "0.375rem",
                      }}
                    >
                      <div style={{ fontWeight: "600", color: "#065f46" }}>Servicio {index + 1}</div>
                      <div style={{ color: "#047857" }}>
                        Expediente: {servicio.expediente || servicio.numeroExpediente || servicio.id || "N/A"}
                      </div>
                      {servicio.fecha && (
                        <div style={{ color: "#047857", fontSize: "0.875rem" }}>Fecha: {servicio.fecha}</div>
                      )}
                      {servicio.hora && (
                        <div style={{ color: "#047857", fontSize: "0.875rem" }}>Hora: {servicio.hora}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            backgroundColor: "#f9fafb",
            padding: "1rem 1.5rem",
            display: "flex",
            justifyContent: "flex-end",
            gap: "1rem",
            borderTop: "1px solid #e2e8f0",
            borderBottomLeftRadius: "0.5rem",
            borderBottomRightRadius: "0.5rem",
          }}
        >
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.5rem 1rem",
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              backgroundColor: "white",
              fontSize: "0.875rem",
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={saveItem}
            disabled={isLoading}
            style={{
              padding: "0.5rem 1rem",
              border: "none",
              borderRadius: "0.375rem",
              background: "linear-gradient(to right, #3b82f6, #4f46e5)",
              color: "white",
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            {isLoading ? (
              <>
                <div
                  style={{
                    width: "1rem",
                    height: "1rem",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    borderTop: "2px solid white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginRight: "0.5rem",
                  }}
                ></div>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
                Guardando...
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: "0.5rem" }}
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                Guardar Servicio
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modal de selección de cliente */}
      {isClientDialogOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "0.5rem",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid #e2e8f0" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", margin: "0 0 0.25rem" }}>Seleccionar Cliente</h3>
              <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: "0" }}>
                Busque y seleccione un cliente de la lista
              </p>
            </div>

            <div style={{ padding: "1rem 1.5rem" }}>
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
                <input
                  type="text"
                  placeholder="Buscar por nombre o identificación..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: "1",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                  }}
                />
                <button
                  style={{
                    padding: "0.5rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "#3b82f6",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              </div>

              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.375rem",
                }}
              >
                {isLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "80px",
                    }}
                  >
                    <div
                      style={{
                        width: "1.5rem",
                        height: "1.5rem",
                        border: "2px solid rgba(59, 130, 246, 0.3)",
                        borderTop: "2px solid #3b82f6",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    ></div>
                    <style>{`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}</style>
                  </div>
                ) : filteredClients.length > 0 ? (
                  <div style={{ borderTop: "1px solid #e2e8f0" }}>
                    {filteredClients.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => handleClientSelect(client.id)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "1rem",
                          border: "none",
                          borderBottom: "1px solid #e2e8f0",
                          backgroundColor: "white",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "white")}
                      >
                        <div>
                          <div style={{ fontWeight: "500" }}>
                            {client.nombre} {client.apellido}
                          </div>
                          <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>{client.Identificacion}</div>
                        </div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            padding: "0.25rem 0.5rem",
                            borderRadius: "9999px",
                            border: "1px solid #d1d5db",
                          }}
                        >
                          Seleccionar
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "1rem",
                      textAlign: "center",
                      color: "#6b7280",
                    }}
                  >
                    No se encontraron clientes
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: "1rem 1.5rem",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setIsClientDialogOpen(false)}
                style={{
                  padding: "0.5rem 1rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  backgroundColor: "white",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal del mapa */}
      {isMapModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "0.5rem",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid #e2e8f0" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", margin: "0 0 0.25rem" }}>
                Seleccionar Ubicación en el Mapa
              </h3>
              <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: "0" }}>
                Haga clic en el mapa o arrastre el marcador para seleccionar la ubicación
              </p>
            </div>

            <div style={{ padding: "1rem 1.5rem" }}>
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
                <input
                  type="text"
                  placeholder="Buscar dirección..."
                  value={mapSearchAddress}
                  onChange={handleMapSearchInputChange}
                  style={{
                    flex: "1",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      searchAddress()
                    }
                  }}
                />
                <button
                  onClick={searchAddress}
                  style={{
                    padding: "0.5rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.375rem",
                    backgroundColor: "#3b82f6",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              </div>

              <div
                ref={mapRef}
                style={{
                  height: "400px",
                  width: "100%",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.375rem",
                }}
              ></div>

              {isLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    padding: "1rem",
                    borderRadius: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                      border: "2px solid rgba(59, 130, 246, 0.3)",
                      borderTop: "2px solid #3b82f6",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  <span>Buscando dirección...</span>
                </div>
              )}
            </div>

            <div
              style={{
                padding: "1rem 1.5rem",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem",
              }}
            >
              <button
                onClick={closeMapModal}
                style={{
                  padding: "0.5rem 1rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  backgroundColor: "white",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmMapLocation}
                style={{
                  padding: "0.5rem 1rem",
                  border: "none",
                  borderRadius: "0.375rem",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Confirmar Ubicación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualización de ruta */}
      {isRouteModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "0.5rem",
              width: "100%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid #e2e8f0" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", margin: "0 0 0.25rem" }}>Trayecto del Servicio</h3>
              <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: "0" }}>
                Ruta calculada entre el origen y destino
              </p>
            </div>

            <div style={{ padding: "1rem 1.5rem" }}>
              {/* Información de la ruta */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1rem",
                  marginBottom: "1rem",
                  padding: "1rem",
                  backgroundColor: "#f8fafc",
                  borderRadius: "0.5rem",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase", fontWeight: "500" }}>
                    Distancia
                  </div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#1f2937", marginTop: "0.25rem" }}>
                    {routeDistance}
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase", fontWeight: "500" }}>
                    Tiempo Estimado
                  </div>
                  <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#1f2937", marginTop: "0.25rem" }}>
                    {routeDuration}
                  </div>
                </div>
              </div>

              {/* Direcciones */}
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      backgroundColor: "#10b981",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    A
                  </div>
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>Origen</div>
                    <div style={{ fontSize: "0.875rem", fontWeight: "500" }}>{formValues.direccionInicial}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      backgroundColor: "#ef4444",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    B
                  </div>
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>Destino</div>
                    <div style={{ fontSize: "0.875rem", fontWeight: "500" }}>{formValues.direccionFinal}</div>
                  </div>
                </div>
              </div>

              {/* Mapa */}
              <div
                ref={routeMapRef}
                style={{
                  height: "400px",
                  width: "100%",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.375rem",
                }}
              ></div>
            </div>

            <div
              style={{
                padding: "1rem 1.5rem",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setIsRouteModalOpen(false)}
                style={{
                  padding: "0.5rem 1rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  backgroundColor: "white",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
